import { useState, useEffect, useRef } from 'react';
import './index.css';

/* ─── TRANSLATIONS ─── */
const T = {
  en: {
    langLabel: 'EN', langFlag: '🇬🇧', langNative: 'English',
    navLinks: ['Problem', 'Features', 'How It Works', 'Reviews'],
    navCta: 'Download App',
    heroBadge: 'Now available across India',
    heroTitle1: 'No more walking to',
    heroTitle2: 'check the menu.',
    heroTitle3: 'It comes to you.',
    heroDesc: 'SwaadIndia brings the daily mess menu straight to your phone. For students, bachelors, and the mess owners who feed them — across India.',
    heroCta1: 'Get on Play Store',
    heroCta2: 'See How It Works',
    heroTrust: '10,000+ users across India',
    probTag: '01 — The Problem',
    probTitle: 'Every student knows this frustration.',
    probSub: 'You move to a new city, find a mess nearby, and then spend every morning wondering — is it open? What are they cooking? Am I going to waste a trip?',
    probs: [
      { e: '🚶‍♂️', title: 'No Menu Visibility', desc: "You have zero idea what's being cooked until you physically walk there — every single day.", color: 'var(--coral)' },
      { e: '❓', title: 'Is It Even Open?', desc: "No way to know if serving mode is on, off, or if there's a holiday closure. Pure guesswork.", color: 'var(--amber)' },
      { e: '📋', title: 'Manual Attendance', desc: 'Paper registers, disputes over remaining meals. No accountability, no transparency.', color: '#9D8EFA' },
    ],
    probSolve: 'SwaadIndia solves all three.',
    featTag: '02 — Features',
    featTitle: 'One platform.',
    featTitleEm: 'Two sides of the table.',
    featDesc: 'Students get real-time menu updates. Vendors get organised operations. Everyone benefits.',
    tabUser: 'For Students & Users',
    tabVendor: 'For Mess Owners',
    userFeats: [
      { i: '📍', t: 'Find Nearby Messes', d: 'Search by area, filter by Veg or Non-Veg. See every mess in your locality — before you step out.', c: 'var(--coral)' },
      { i: '🍽', t: "Today's Menu, Live", d: "Know exactly what's on the menu before you leave the house. Real-time updates, every day.", c: 'var(--amber)' },
      { i: '💳', t: 'Flexible Subscriptions', d: "Monthly meal plans that work around your life. Pause when you travel. Resume anytime.", c: '#9D8EFA' },
      { i: '📱', t: 'QR Attendance Check-in', d: 'Scan QR at mealtime. Attendance marked instantly. Remaining meals always up to date.', c: 'var(--mint)' },
    ],
    vendorFeats: [
      { i: '📢', t: 'Post Menu in Seconds', d: "Add today's rice plate or thali with a single tap. All your customers are notified immediately.", c: 'var(--coral)' },
      { i: '⭐', t: 'Special Announcements', d: 'Festival menus, Sunday specials, holiday notices — sent to all subscribers in one go.', c: 'var(--amber)' },
      { i: '🔲', t: 'Smart QR Attendance', d: 'Generate a unique QR per session. Customers scan — attendance logged, no disputes.', c: '#9D8EFA' },
      { i: '📊', t: 'Reports & Analytics', d: 'Attendance history, subscription usage, daily inquiry count. Your business, at a glance.', c: 'var(--mint)' },
    ],
    qrTag: '03 — Attendance',
    qrTitle: 'Scan. Done.',
    qrTitleEm: 'No registers needed.',
    qrDesc: "The mess attendance problem is solved end-to-end. Vendors generate a QR code, students scan it — everything is logged automatically, in real time.",
    qrFeats: [
      { t: 'Vendor generates a unique QR per slot', d: 'Separate codes for lunch and dinner. Every session tracked independently.' },
      { t: 'Student scans at mealtime', d: 'Open app, point at QR code, done. Attendance is marked in under 2 seconds.' },
      { t: 'Vendor gets a full attendance report', d: "Who came, who didn't, meals remaining per student — all in the Reports tab." },
      { t: 'Student tracks their own balance', d: 'Days used, days remaining, plan expiry — always visible on the home screen.' },
    ],
    qrStats: ['100%', 'Paperless', '0', 'Disputes', 'Real', 'Time Track', 'Auto', 'Reports'],
    stats: [
      { num: '10K+', label: 'Active Users', color: 'var(--coral)' },
      { num: '500+', label: 'Mess Vendors', color: 'var(--amber)' },
      { num: '29', label: 'States', color: '#9D8EFA' },
      { num: '4.5★', label: 'Play Store', color: 'var(--mint)' },
    ],
    howTag: '04 — How It Works',
    howTitle: 'Up and running',
    howTitleEm: 'in four steps.',
    tabUserHow: 'For Students',
    tabVendorHow: 'For Vendors',
    userSteps: [
      ['Download & Sign Up', 'Install SwaadIndia from Play Store. Enter your city and set your food preference — Veg or Non-Veg.'],
      ['Find Your Mess', "Browse messes in your area. Check who's open today and see the live menu before you go."],
      ['Subscribe to a Plan', 'Choose a monthly lunch or dinner plan. Pause it anytime when you travel home.'],
      ['Scan QR & Eat', 'Arrive at the mess, scan the QR code — attendance is marked and your meal count updates.'],
    ],
    vendorSteps: [
      ['Register Your Mess', 'Sign up as a vendor. Add your name, location, cuisine type, and pricing. Go live instantly.'],
      ["Post Today's Menu", "Add the day's thali each morning with one tap. Every subscribed customer gets notified."],
      ['Set Up QR at Counter', 'Display your generated QR code at the entrance. Customers scan — fully automated.'],
      ['Track & Grow', 'Review daily reports, manage subscriptions, and watch your inquiry count grow.'],
    ],
    testiTag: '05 — Testimonials',
    testiTitle: 'Real people.',
    testiTitleEm: 'Real problems solved.',
    testis: [
      { name: 'Rahul Patil', role: 'Student, Pune', type: 'Student', text: '"Pehle roz mess tak jaata tha sirf menu dekhne. Ab ghar se hi pata chal jaata hai. Seriously game-changing."', stars: 5 },
      { name: 'Suresh Bhai', role: 'Mess Owner, Nagpur', type: 'Vendor', text: '"QR attendance system ne meri life easy kar di. No disputes, no register. Everything is automatic now."', stars: 5 },
      { name: 'Amit Kumar', role: 'Working Professional, Mumbai', type: 'Bachelor', text: '"New city, no contacts. SwaadIndia helped me find a good mess nearby. The pause feature is brilliant."', stars: 5 },
      { name: 'Mangal Tai', role: 'Mess Owner, Pune', type: 'Vendor', text: '"After putting the menu on the app, new customers started coming on their own. Business is growing."', stars: 5 },
      { name: 'Karan Mehta', role: 'Engineering Student, Nashik', type: 'Student', text: '"I get a notification with today\'s menu. If I don\'t like it, I eat at home. Saves both time and money."', stars: 5 },
      { name: 'Prashant Yadav', role: 'Bachelor, Bengaluru', type: 'Bachelor', text: '"I pause my plan whenever I go home. Not a single meal goes to waste. No other app had this."', stars: 5 },
    ],
    dlTag: 'Download',
    dlTitle: 'Your mess menu,\nalways one tap away.',
    dlDesc: 'Free to download. Thousands of messes. Available across India.',
    dlNote: 'Available on Android · iOS coming soon',
    footerTagline: "India's mess & tiffin discovery platform.",
    footerCols: [
      { title: 'Product', links: ['For Students', 'For Vendors', 'How It Works', 'Download'] },
      { title: 'Company', links: ['About Us', 'Blog', 'Contact', 'Careers'] },
      { title: 'Legal', links: ['Privacy Policy', 'Terms of Use', 'Refund Policy'] },
    ],
    footerCopy: '© 2026 SwaadIndia. Made with care in India.',
    footerCities: 'Pune · Mumbai · Nagpur · Nashik · Bengaluru · Pan India',
  },
  hi: {
    langLabel: 'HI', langFlag: '🇮🇳', langNative: 'हिंदी',
    navLinks: ['समस्या', 'फीचर्स', 'कैसे काम करे', 'रिव्यू'],
    navCta: 'ऐप डाउनलोड करें',
    heroBadge: 'अब पूरे भारत में उपलब्ध',
    heroTitle1: 'अब मेनू देखने',
    heroTitle2: 'मेस नहीं जाना।',
    heroTitle3: 'मेनू आपके पास आएगा।',
    heroDesc: 'SwaadIndia रोज़ाना मेस का मेनू सीधे आपके फोन पर लाता है। छात्रों, बैचलर्स और मेस मालिकों के लिए — पूरे भारत में।',
    heroCta1: 'Play Store पर पाएं',
    heroCta2: 'कैसे काम करता है देखें',
    heroTrust: 'पूरे भारत में 10,000+ उपयोगकर्ता',
    probTag: '01 — समस्या',
    probTitle: 'हर छात्र इस परेशानी को जानता है।',
    probSub: 'नए शहर में मेस ढूंढते हो, फिर हर सुबह सोचते हो — खुला होगा? क्या बन रहा होगा? यात्रा बर्बाद तो नहीं होगी?',
    probs: [
      { e: '🚶‍♂️', title: 'मेनू की जानकारी नहीं', desc: 'जब तक खुद जाकर नहीं देखते, पता नहीं चलता आज क्या बन रहा है।', color: 'var(--coral)' },
      { e: '❓', title: 'खुला भी है?', desc: 'पता नहीं सर्विंग मोड चालू है, बंद है या छुट्टी है। पूरा अंदाज़ा।', color: 'var(--amber)' },
      { e: '📋', title: 'मैन्युअल अटेंडेंस', desc: 'पेपर रजिस्टर, बचे मील पर झगड़े। कोई जवाबदेही नहीं।', color: '#9D8EFA' },
    ],
    probSolve: 'SwaadIndia तीनों समस्याएं हल करता है।',
    featTag: '02 — फीचर्स',
    featTitle: 'एक प्लेटफॉर्म।',
    featTitleEm: 'दोनों के लिए।',
    featDesc: 'छात्रों को रियल-टाइम मेनू अपडेट। वेंडर को व्यवस्थित संचालन।',
    tabUser: 'छात्रों और यूजर्स के लिए',
    tabVendor: 'मेस मालिकों के लिए',
    userFeats: [
      { i: '📍', t: 'नज़दीकी मेस खोजें', d: 'एरिया से सर्च करें, वेज/नॉन-वेज फ़िल्टर करें। बाहर जाने से पहले सब देखें।', c: 'var(--coral)' },
      { i: '🍽', t: 'आज का मेनू, लाइव', d: 'घर से निकलने से पहले मेनू पता करें। रियल-टाइम अपडेट, रोज़।', c: 'var(--amber)' },
      { i: '💳', t: 'लचीले सब्सक्रिप्शन', d: 'मासिक प्लान। यात्रा पर पॉज़ करें। कभी भी फिर शुरू करें।', c: '#9D8EFA' },
      { i: '📱', t: 'QR अटेंडेंस चेक-इन', d: 'खाने पर QR स्कैन करें। अटेंडेंस तुरंत दर्ज। बचे मील हमेशा अपडेट।', c: 'var(--mint)' },
    ],
    vendorFeats: [
      { i: '📢', t: 'सेकंड में मेनू पोस्ट करें', d: 'एक टैप में आज की थाली जोड़ें। सभी ग्राहकों को तुरंत सूचना।', c: 'var(--coral)' },
      { i: '⭐', t: 'विशेष ऐलान', d: 'त्योहार मेनू, रविवार स्पेशल, छुट्टी नोटिस — एक साथ सबको भेजें।', c: 'var(--amber)' },
      { i: '🔲', t: 'स्मार्ट QR अटेंडेंस', d: 'प्रति सेशन यूनिक QR जेनरेट करें। ग्राहक स्कैन करें — कोई झगड़ा नहीं।', c: '#9D8EFA' },
      { i: '📊', t: 'रिपोर्ट और एनालिटिक्स', d: 'अटेंडेंस इतिहास, सब्सक्रिप्शन उपयोग, रोज़ाना इंक्वायरी। सब कुछ स्पष्ट।', c: 'var(--mint)' },
    ],
    qrTag: '03 — अटेंडेंस',
    qrTitle: 'स्कैन। हो गया।',
    qrTitleEm: 'रजिस्टर की ज़रूरत नहीं।',
    qrDesc: 'मेस अटेंडेंस की समस्या शुरू से आखिर तक हल। वेंडर QR बनाते हैं, छात्र स्कैन करते हैं — सब कुछ ऑटोमेटिक।',
    qrFeats: [
      { t: 'वेंडर प्रति स्लॉट यूनिक QR बनाता है', d: 'लंच और डिनर के लिए अलग कोड। हर सेशन अलग से ट्रैक।' },
      { t: 'छात्र खाने के समय स्कैन करता है', d: 'ऐप खोलें, QR पर कैमरा। 2 सेकंड में अटेंडेंस दर्ज।' },
      { t: 'वेंडर को पूरी अटेंडेंस रिपोर्ट मिलती है', d: 'कौन आया, कौन नहीं, बचे मील — सब रिपोर्ट टैब में।' },
      { t: 'छात्र अपना बैलेंस ट्रैक करता है', d: 'उपयोग किए दिन, बचे दिन, एक्सपायरी — होम स्क्रीन पर।' },
    ],
    qrStats: ['100%', 'पेपरलेस', '0', 'विवाद नहीं', 'रियल', 'टाइम ट्रैक', 'ऑटो', 'रिपोर्ट'],
    stats: [
      { num: '10K+', label: 'सक्रिय यूजर', color: 'var(--coral)' },
      { num: '500+', label: 'मेस वेंडर', color: 'var(--amber)' },
      { num: '29', label: 'राज्य', color: '#9D8EFA' },
      { num: '4.5★', label: 'Play Store', color: 'var(--mint)' },
    ],
    howTag: '04 — कैसे काम करे',
    howTitle: 'चार कदम में',
    howTitleEm: 'शुरू हो जाइए।',
    tabUserHow: 'छात्रों के लिए',
    tabVendorHow: 'वेंडर के लिए',
    userSteps: [
      ['डाउनलोड और साइन अप', 'Play Store से SwaadIndia इंस्टॉल करें। शहर और पसंद चुनें — वेज या नॉन-वेज।'],
      ['मेस खोजें', 'अपने एरिया में मेस देखें। आज कौन खुला है और मेनू क्या है — पहले से देखें।'],
      ['प्लान सब्सक्राइब करें', 'मासिक लंच या डिनर प्लान चुनें। यात्रा पर जाएं तो पॉज़ करें।'],
      ['QR स्कैन करें और खाएं', 'मेस पहुंचें, QR स्कैन करें — अटेंडेंस दर्ज, मील काउंट अपडेट।'],
    ],
    vendorSteps: [
      ['मेस रजिस्टर करें', 'वेंडर के रूप में साइन अप करें। नाम, लोकेशन, मेनू टाइप और प्राइसिंग जोड़ें।'],
      ['आज का मेनू पोस्ट करें', 'हर सुबह एक टैप में थाली जोड़ें। सभी सब्सक्राइब्ड ग्राहकों को नोटिफिकेशन।'],
      ['काउंटर पर QR लगाएं', 'जेनरेटेड QR कोड एंट्रेंस पर लगाएं। ग्राहक स्कैन करें — पूरी तरह ऑटोमेटिक।'],
      ['ट्रैक करें और बढ़ें', 'डेली रिपोर्ट देखें, सब्सक्रिप्शन मैनेज करें, इंक्वायरी बढ़ते देखें।'],
    ],
    testiTag: '05 — रिव्यू',
    testiTitle: 'असली लोग।',
    testiTitleEm: 'असली समस्या हल।',
    testis: [
      { name: 'राहुल पाटिल', role: 'छात्र, पुणे', type: 'छात्र', text: '"पहले रोज़ मेस तक जाता था सिर्फ मेनू देखने। अब घर से ही पता चल जाता है। सच में ज़िंदगी बदल दी।"', stars: 5 },
      { name: 'सुरेश भाई', role: 'मेस मालिक, नागपुर', type: 'वेंडर', text: '"QR सिस्टम ने सब आसान कर दिया। कोई झगड़ा नहीं, कोई रजिस्टर नहीं। सब ऑटोमेटिक।"', stars: 5 },
      { name: 'अमित कुमार', role: 'प्रोफेशनल, मुंबई', type: 'बैचलर', text: '"नया शहर था। SwaadIndia से अच्छा मेस ढूंढा। पॉज़ फीचर सच में बेहतरीन है।"', stars: 5 },
      { name: 'मंगल ताई', role: 'मेस मालिक, पुणे', type: 'वेंडर', text: '"ऐप पर मेनू डाला तो खुद नए ग्राहक आने लगे। बिज़नेस बढ़ रहा है।"', stars: 5 },
      { name: 'करण मेहता', role: 'इंजीनियरिंग छात्र, नाशिक', type: 'छात्र', text: '"नोटिफिकेशन से मेनू पता चलता है। पसंद न हो तो घर पर खाना बना लो। समय और पैसे दोनों बचते हैं।"', stars: 5 },
      { name: 'प्रशांत यादव', role: 'बैचलर, बेंगलुरु', type: 'बैचलर', text: '"घर जाने पर पॉज़ करता हूं। एक भी मील बर्बाद नहीं होती। किसी ऐप में यह नहीं था।"', stars: 5 },
    ],
    dlTag: 'डाउनलोड',
    dlTitle: 'आपका मेस मेनू,\nहमेशा एक टैप दूर।',
    dlDesc: 'मुफ्त डाउनलोड। हज़ारों मेस। पूरे भारत में उपलब्ध।',
    dlNote: 'Android पर उपलब्ध · iOS जल्द आएगा',
    footerTagline: 'भारत का मेस और टिफिन डिस्कवरी प्लेटफॉर्म।',
    footerCols: [
      { title: 'प्रोडक्ट', links: ['छात्रों के लिए', 'वेंडर के लिए', 'कैसे काम करे', 'डाउनलोड'] },
      { title: 'कंपनी', links: ['हमारे बारे में', 'ब्लॉग', 'संपर्क', 'करियर'] },
      { title: 'कानूनी', links: ['गोपनीयता नीति', 'उपयोग शर्तें', 'रिफंड नीति'] },
    ],
    footerCopy: '© 2026 SwaadIndia। भारत में बना।',
    footerCities: 'पुणे · मुंबई · नागपुर · नाशिक · बेंगलुरु · पूरे भारत में',
  },
  mr: {
    langLabel: 'MR', langFlag: '🇮🇳', langNative: 'मराठी',
    navLinks: ['समस्या', 'वैशिष्ट्ये', 'कसे काम करते', 'रिव्ह्यू'],
    navCta: 'ऐप डाउनलोड करा',
    heroBadge: 'आता संपूर्ण भारतात उपलब्ध',
    heroTitle1: 'आता मेनू पाहण्यासाठी',
    heroTitle2: 'मेसला जाण्याची गरज नाही।',
    heroTitle3: 'मेनू तुमच्याकडे येतो।',
    heroDesc: 'SwaadIndia रोजचे मेस मेनू थेट तुमच्या फोनवर आणते. विद्यार्थी, बॅचलर्स आणि मेस मालकांसाठी — संपूर्ण भारतात.',
    heroCta1: 'Play Store वर मिळवा',
    heroCta2: 'कसे काम करते पहा',
    heroTrust: 'भारतात 10,000+ वापरकर्ते',
    probTag: '01 — समस्या',
    probTitle: 'प्रत्येक विद्यार्थ्याला ही चिडचिड माहीत आहे.',
    probSub: 'नवीन शहरात मेस सापडतो, मग रोज सकाळी प्रश्न पडतो — उघडे असेल का? काय बनत असेल? चालत जाणे वाया जाईल का?',
    probs: [
      { e: '🚶‍♂️', title: 'मेनूची माहिती नाही', desc: 'स्वतः जाऊन पाहिल्याशिवाय आज काय बनत आहे हे कळत नाही.', color: 'var(--coral)' },
      { e: '❓', title: 'उघडे आहे का?', desc: 'सर्व्हिंग मोड चालू आहे, बंद आहे, की सुट्टी आहे हे माहीत नसते.', color: 'var(--amber)' },
      { e: '📋', title: 'मॅन्युअल हजेरी', desc: 'कागदी रजिस्टर, उरलेल्या जेवणावर वाद. जबाबदारी नाही, पारदर्शकता नाही.', color: '#9D8EFA' },
    ],
    probSolve: 'SwaadIndia तिन्ही समस्या सोडवते.',
    featTag: '02 — वैशिष्ट्ये',
    featTitle: 'एक प्लॅटफॉर्म।',
    featTitleEm: 'दोन्हींसाठी।',
    featDesc: 'विद्यार्थ्यांना रिअल-टाइम मेनू. विक्रेत्यांना सुव्यवस्थित कारभार.',
    tabUser: 'विद्यार्थी व वापरकर्त्यांसाठी',
    tabVendor: 'मेस मालकांसाठी',
    userFeats: [
      { i: '📍', t: 'जवळचे मेस शोधा', d: 'भागानुसार शोधा, शाकाहारी/मांसाहारी फिल्टर करा. बाहेर पडण्यापूर्वी सर्व पाहा.', c: 'var(--coral)' },
      { i: '🍽', t: 'आजचा मेनू, थेट', d: 'घराबाहेर पडण्यापूर्वी मेनू जाणून घ्या. रिअल-टाइम अपडेट, रोज.', c: 'var(--amber)' },
      { i: '💳', t: 'लवचिक सदस्यता', d: 'मासिक जेवण योजना. प्रवासात थांबवा. कधीही पुन्हा सुरू करा.', c: '#9D8EFA' },
      { i: '📱', t: 'QR हजेरी चेक-इन', d: 'जेवणाच्या वेळी QR स्कॅन करा. हजेरी लगेच नोंद. उरलेले जेवण नेहमी अपडेट.', c: 'var(--mint)' },
    ],
    vendorFeats: [
      { i: '📢', t: 'सेकंदात मेनू पोस्ट करा', d: 'एका टॅपने आजची थाळी जोडा. सर्व ग्राहकांना लगेच सूचना.', c: 'var(--coral)' },
      { i: '⭐', t: 'विशेष जाहिराती', d: 'सणांचे मेनू, रविवार स्पेशल, सुट्टी नोटीस — एकाच वेळी सर्वांना.', c: 'var(--amber)' },
      { i: '🔲', t: 'स्मार्ट QR हजेरी', d: 'प्रति सत्र अद्वितीय QR तयार करा. ग्राहक स्कॅन करतात — कोणताही वाद नाही.', c: '#9D8EFA' },
      { i: '📊', t: 'अहवाल व विश्लेषण', d: 'हजेरी इतिहास, सदस्यता वापर, रोजची चौकशी. सर्व माहिती एका दृष्टिक्षेपात.', c: 'var(--mint)' },
    ],
    qrTag: '03 — हजेरी',
    qrTitle: 'स्कॅन. झाले।',
    qrTitleEm: 'रजिस्टरची गरज नाही।',
    qrDesc: 'मेस हजेरीची समस्या शेवटपर्यंत सुटली. विक्रेते QR तयार करतात, विद्यार्थी स्कॅन करतात — सर्व काही आपोआप.',
    qrFeats: [
      { t: 'विक्रेता प्रति स्लॉट QR तयार करतो', d: 'दुपार आणि रात्रीसाठी वेगळे कोड. प्रत्येक सत्र स्वतंत्रपणे ट्रॅक.' },
      { t: 'विद्यार्थी जेवणाच्या वेळी स्कॅन करतो', d: 'ऐप उघडा, QR वर कॅमेरा. 2 सेकंदात हजेरी नोंद.' },
      { t: 'विक्रेत्याला संपूर्ण हजेरी अहवाल', d: 'कोण आले, कोण नाही, उरलेले जेवण — सर्व अहवाल टॅबमध्ये.' },
      { t: 'विद्यार्थी स्वतःचे जेवण ट्रॅक करतो', d: 'वापरलेले दिवस, उरलेले, समाप्ती — होम स्क्रीनवर नेहमी.' },
    ],
    qrStats: ['100%', 'कागदरहित', '0', 'वाद नाही', 'रिअल', 'टाइम ट्रॅक', 'आपोआप', 'अहवाल'],
    stats: [
      { num: '10K+', label: 'सक्रिय वापरकर्ते', color: 'var(--coral)' },
      { num: '500+', label: 'मेस विक्रेते', color: 'var(--amber)' },
      { num: '29', label: 'राज्ये', color: '#9D8EFA' },
      { num: '4.5★', label: 'Play Store', color: 'var(--mint)' },
    ],
    howTag: '04 — कसे काम करते',
    howTitle: 'चार पायऱ्यांत',
    howTitleEm: 'सुरुवात करा।',
    tabUserHow: 'विद्यार्थ्यांसाठी',
    tabVendorHow: 'विक्रेत्यांसाठी',
    userSteps: [
      ['डाउनलोड आणि साइन अप', 'Play Store वरून SwaadIndia इन्स्टॉल करा. शहर आणि पसंती निवडा.'],
      ['मेस शोधा', 'तुमच्या भागात मेस पाहा. आज कोण उघडे आहे आणि मेनू काय आहे — आधीच पाहा.'],
      ['योजना सदस्यता घ्या', 'मासिक दुपार किंवा रात्रीच्या जेवणाची योजना निवडा. प्रवासात थांबवा.'],
      ['QR स्कॅन करा आणि खा', 'मेसवर पोहोचा, QR स्कॅन करा — हजेरी नोंद, जेवण काउंट अपडेट.'],
    ],
    vendorSteps: [
      ['मेस नोंदणी करा', 'विक्रेता म्हणून साइन अप करा. नाव, स्थान, मेनू प्रकार आणि किंमत जोडा.'],
      ['आजचा मेनू पोस्ट करा', 'दररोज सकाळी एका टॅपने थाळी जोडा. सर्व सदस्यांना सूचना मिळते.'],
      ['काउंटरवर QR लावा', 'तयार केलेला QR कोड प्रवेशद्वारावर लावा. ग्राहक स्कॅन करतात — पूर्णपणे स्वयंचलित.'],
      ['ट्रॅक करा आणि वाढा', 'दैनंदिन अहवाल पाहा, सदस्यता व्यवस्थापित करा, चौकशी वाढताना पाहा.'],
    ],
    testiTag: '05 — रिव्ह्यू',
    testiTitle: 'खरे लोक।',
    testiTitleEm: 'खरी समस्या सुटली।',
    testis: [
      { name: 'राहुल पाटील', role: 'विद्यार्थी, पुणे', type: 'विद्यार्थी', text: '"आधी रोज मेसला जायचो फक्त मेनू पाहायला. आता घरून कळतं काय बनतंय. खरंच जीव वाचवणारं."', stars: 5 },
      { name: 'सुरेश भाऊ', role: 'मेस मालक, नागपूर', type: 'विक्रेता', text: '"QR प्रणालीने सर्व सोपे झाले. कोणताही वाद नाही, कोणताही रजिस्टर नाही. सर्व स्वयंचलित."', stars: 5 },
      { name: 'अमित कुमार', role: 'व्यावसायिक, मुंबई', type: 'बॅचलर', text: '"नवीन शहर होते. SwaadIndia ने चांगले मेस शोधण्यास मदत केली. पॉज फीचर खरोखर उत्कृष्ट आहे."', stars: 5 },
      { name: 'मंगल ताई', role: 'मेस मालक, पुणे', type: 'विक्रेता', text: '"ऐपवर मेनू टाकल्यावर नवीन ग्राहक आपोआप आले. व्यवसाय वाढतोय."', stars: 5 },
      { name: 'करण मेहता', role: 'इंजिनीअरिंग विद्यार्थी, नाशिक', type: 'विद्यार्थी', text: '"नोटिफिकेशनने मेनू कळतो. आवडले नाही तर घरी बनवतो. वेळ आणि पैसे दोन्ही वाचतात."', stars: 5 },
      { name: 'प्रशांत यादव', role: 'बॅचलर, बेंगळुरू', type: 'बॅचलर', text: '"घरी गेल्यावर पॉज करतो. एकही जेवण वाया जात नाही. दुसऱ्या कुठल्या ऐपमध्ये हे नव्हते."', stars: 5 },
    ],
    dlTag: 'डाउनलोड',
    dlTitle: 'तुमचे मेस मेनू,\nनेहमी एका टॅपवर।',
    dlDesc: 'मोफत डाउनलोड. हजारो मेस. संपूर्ण भारतात उपलब्ध.',
    dlNote: 'Android वर उपलब्ध · iOS लवकरच',
    footerTagline: 'भारताचे मेस आणि टिफिन डिस्कव्हरी प्लॅटफॉर्म.',
    footerCols: [
      { title: 'उत्पादन', links: ['विद्यार्थ्यांसाठी', 'विक्रेत्यांसाठी', 'कसे काम करते', 'डाउनलोड'] },
      { title: 'कंपनी', links: ['आमच्याबद्दल', 'ब्लॉग', 'संपर्क', 'करिअर'] },
      { title: 'कायदेशीर', links: ['गोपनीयता धोरण', 'वापराच्या अटी', 'परतावा धोरण'] },
    ],
    footerCopy: '© 2026 SwaadIndia. भारतात बनवले.',
    footerCities: 'पुणे · मुंबई · नागपूर · नाशिक · बेंगळुरू · संपूर्ण भारतात',
  },
};

/* ─── useReveal hook ─── */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.classList.add('in'); obs.disconnect(); } }, { threshold: 0.1 });
    obs.observe(el); return () => obs.disconnect();
  }, []);
  return ref;
}

/* ─── Phone Screens ─── */
function UserScreen() {
  return (
    <div>
      <div className="us-header">
        <div className="us-header-top">
          <div><div className="us-welcome">Welcome</div><div className="us-name">Rahul Sharma</div></div>
          <div className="us-avatar">RS</div>
        </div>
        <div className="us-date">Tuesday, Mar 31, 2026</div>
      </div>
      <div className="us-body">
        <div className="us-search">🔍 Search area / Hotel / Mess...</div>
        <div className="us-filters">
          <div className="us-filter filter-city">📍 Pune ▾</div>
          <div className="us-filter filter-nonveg">Non-Veg</div>
          <div className="us-filter filter-veg">Pure Veg</div>
        </div>
        <div className="plan-card">
          <div className="plan-label">Active Plan</div>
          <div className="plan-name">Sharma Mess</div>
          <div className="plan-type-badge">LUNCH</div>
          <div className="plan-bar-bg"><div className="plan-bar-fill" /></div>
          <div className="plan-stats">
            <div className="plan-stat"><div className="ps-num">2</div><div className="ps-label">Used</div></div>
            <div className="ps-divider" />
            <div className="plan-stat"><div className="ps-num green">28</div><div className="ps-label">Left</div></div>
            <div className="ps-divider" />
            <div className="plan-stat"><div className="ps-num">30</div><div className="ps-label">Total</div></div>
          </div>
        </div>
        <div className="us-sec-label">Vendors Near You</div>
        <div className="vendor-card-mini">
          <div className="vcm-name">Sharma Mess</div>
          <div className="vcm-meta">🍽 Pure Veg · 📍 Wakad, Pune</div>
          <div className="vcm-badge badge-open">Dal Chawal, Sabji, Roti</div>
        </div>
        <div className="vendor-card-mini">
          <div className="vcm-name">Vendor3 Mess</div>
          <div className="vcm-meta">🍽 Veg/Non-Veg · 📍 Akurdi</div>
          <div className="vcm-badge badge-closed">Currently Closed</div>
        </div>
      </div>
    </div>
  );
}

function VendorScreen() {
  const grid = [['🍽','Add Menu','var(--coral)'],['⭐','Special','var(--amber)'],['👥','Attendance','#9D8EFA'],['💳','Subscriptions','var(--mint)'],['🕐','History','#FF7043'],['📊','Reports','#29B6F6']];
  return (
    <div>
      <div className="vs-header">
        <div className="vs-header-top">
          <div><div className="vs-welcome">Welcome,</div><div className="vs-name">Sharma Mess</div></div>
          <div className="vs-avatar">SM</div>
        </div>
      </div>
      <div className="vs-body">
        <div className="vs-inq-card"><div className="vs-inq-label">Interest Today</div><div className="vs-inq-val">3 Inquiries</div></div>
        <div className="vs-status-card">
          <div className="vs-status-row">
            <div><div className="vs-status-title">Current Status</div><div className="vs-status-date">Tue, 31 Mar 2026</div></div>
            <div className="vs-toggle"><div className="vs-toggle-dot" /></div>
          </div>
          <div className="vs-mode">Serving Mode — On</div>
        </div>
        <div className="vs-grid">
          {grid.map(([icon, label, color], i) => (
            <div key={i} className="vs-grid-btn" style={{ borderLeft: `2px solid ${color}` }}>
              <div className="vs-grid-icon">{icon}</div>
              <div className="vs-grid-label">{label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PhoneMock({ children, className = '' }) {
  return (
    <div className={className}>
      <div className="phone-outer">
        <div className="phone-screen-wrap">{children}</div>
      </div>
    </div>
  );
}

/* ─── Language Dropdown ─── */
function LangDropdown({ lang, setLang }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const options = [
    { code: 'en', label: 'EN', flag: '🇬🇧', name: 'English', native: 'English' },
    { code: 'hi', label: 'HI', flag: '🇮🇳', name: 'Hindi', native: 'हिंदी' },
    { code: 'mr', label: 'MR', flag: '🇮🇳', name: 'Marathi', native: 'मराठी' },
  ];
  const current = options.find(o => o.code === lang);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="lang-wrap" ref={ref}>
      <button className="lang-trigger" onClick={() => setOpen(o => !o)}>
        <span className="lang-flag">{current.flag}</span>
        <span>{current.label}</span>
        <i className={`lang-chevron${open ? ' open' : ''}`}>▾</i>
      </button>
      {open && (
        <div className="lang-menu">
          {options.map(opt => (
            <button key={opt.code} className={`lang-opt${lang === opt.code ? ' active' : ''}`}
              onClick={() => { setLang(opt.code); setOpen(false); }}>
              <span className="lang-opt-emoji">{opt.flag}</span>
              <span className="lang-opt-label">{opt.name}</span>
              <span className="lang-opt-native">{opt.native}</span>
              <span className="lang-active-dot" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Navbar ─── */
function Navbar({ lang, setLang, t }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', h); return () => window.removeEventListener('scroll', h);
  }, []);
  const ids = ['problem', 'features', 'howitworks', 'reviews'];
  return (
    <nav className={`navbar${scrolled ? ' scrolled' : ''}`}>
      <div className="navbar-inner">
        <div className="navbar-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <div className="logo-mark">🍽</div>
          <div className="logo-text">Swaad<span>India</span></div>
        </div>
        <ul className="nav-links">
          {t.navLinks.map((l, i) => (
            <li key={i} onClick={() => document.getElementById(ids[i])?.scrollIntoView({ behavior: 'smooth' })}>{l}</li>
          ))}
        </ul>
        <div className="nav-right">
          <LangDropdown lang={lang} setLang={setLang} />
          <button className="nav-cta">{t.navCta}</button>
        </div>
      </div>
    </nav>
  );
}

/* ─── Hero ─── */
function Hero({ t }) {
  return (
    <section className="hero">
      <div className="hero-bg-glow" />
      <div className="hero-grid-overlay" />
      <div className="hero-left">
        <div className="hero-badge"><span className="badge-pulse" />{t.heroBadge}</div>
        <h1 className="hero-title">
          <span>{t.heroTitle1}</span><br />
          <span className="hero-title-muted">{t.heroTitle2}</span><br />
          <span className="hero-title-accent">{t.heroTitle3}</span>
        </h1>
        <p className="hero-desc">{t.heroDesc}</p>
        <div className="hero-actions">
          <button className="btn-primary">▶ {t.heroCta1}</button>
          <button className="btn-secondary" onClick={() => document.getElementById('howitworks')?.scrollIntoView({ behavior: 'smooth' })}>
            {t.heroCta2}
          </button>
        </div>
        <div className="hero-trust">
          <div className="trust-avatars">
            {[['R','#E8480A'],['P','#6B48F5'],['A','#00C896'],['K','#F0A500']].map(([l, c], i) => (
              <div key={i} className="trust-av" style={{ background: c }}>{l}</div>
            ))}
          </div>
          <div className="trust-text"><strong>{t.heroTrust.split(' ')[0]}</strong> {t.heroTrust.split(' ').slice(1).join(' ')}</div>
        </div>
      </div>
      <div className="hero-right">
        <div className="notif-float top">
          <div className="notif-emoji">🍛</div>
          <div className="notif-title">Today's Menu Updated</div>
          <div className="notif-sub">Sharma Mess · <span className="mint">Lunch ready</span></div>
        </div>
        <PhoneMock className="phone-back-left"><VendorScreen /></PhoneMock>
        <PhoneMock className="phone-main"><UserScreen /></PhoneMock>
        <div className="notif-float bottom">
          <div className="notif-emoji">✅</div>
          <div className="notif-title">QR Scan Successful</div>
          <div className="notif-sub">Rahul · <span className="mint">Lunch marked ✓</span></div>
        </div>
      </div>
    </section>
  );
}

/* ─── Marquee ─── */
function Marquee() {
  const items = ['Dal Makhani', 'Masala Dosa', 'Butter Chicken', 'Hyderabadi Biryani', 'Chole Bhature', 'Rajma Chawal', 'Sambar Rice', 'Vada Pav', 'Khichdi', 'Pav Bhaji', 'Paneer Tikka', 'Rogan Josh'];
  const doubled = [...items, ...items];
  return (
    <div className="marquee-bar">
      <div className="marquee-track">
        {doubled.map((item, i) => <span key={i} className="marquee-item">{item}{i < doubled.length - 1 ? <span style={{ margin: '0 0.5rem', opacity: 0.35 }}>·</span> : ''}</span>)}
      </div>
    </div>
  );
}

/* ─── Problem ─── */
function Problem({ t }) {
  const r1 = useReveal(), r2 = useReveal(), r3 = useReveal();
  return (
    <section id="problem" className="problem-section">
      <div className="section-inner">
        <span ref={r1} className="reveal section-tag tag-coral">{t.probTag}</span>
        <h2 ref={r2} className="reveal section-heading large">{t.probTitle}</h2>
        <p ref={r2} className="reveal problem-sub">{t.probSub}</p>
        <div ref={r3} className="reveal problem-grid">
          {t.probs.map((p, i) => (
            <div key={i} className="problem-card"
              onMouseEnter={e => { e.currentTarget.style.borderColor = p.color; e.currentTarget.style.boxShadow = `0 8px 32px rgba(0,0,0,0.25)`; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}>
              <div className="prob-num">{i + 1}</div>
              <div className="prob-icon-wrap" style={{ background: `${p.color}10` }}>{p.e}</div>
              <div className="prob-title">{p.title}</div>
              <div className="prob-desc">{p.desc}</div>
              <div className="prob-line" style={{ background: p.color }} />
            </div>
          ))}
        </div>
        <div className="solve-tag">✓ {t.probSolve}</div>
      </div>
    </section>
  );
}

/* ─── Features ─── */
function Features({ t }) {
  const [tab, setTab] = useState('user');
  const r1 = useReveal(), r2 = useReveal(), r3 = useReveal();
  const feats = tab === 'user' ? t.userFeats : t.vendorFeats;
  return (
    <section id="features" className="features-section">
      <div className="section-inner">
        <div className="features-top-grid">
          <div ref={r1} className="reveal">
            <span className="section-tag tag-amber">{t.featTag}</span>
            <h2 className="section-heading">{t.featTitle} <em>{t.featTitleEm}</em></h2>
            <p className="section-desc">{t.featDesc}</p>
            <div className="audience-tabs">
              <button className={`aud-tab${tab === 'user' ? ' active' : ''}`} onClick={() => setTab('user')}>{t.tabUser}</button>
              <button className={`aud-tab${tab === 'vendor' ? ' active' : ''}`} onClick={() => setTab('vendor')}>{t.tabVendor}</button>
            </div>
          </div>
          <div ref={r2} className="reveal phone-center">
            <PhoneMock className="phone-main">{tab === 'user' ? <UserScreen /> : <VendorScreen />}</PhoneMock>
          </div>
        </div>
        <div ref={r3} className="reveal features-grid">
          {feats.map((f, i) => (
            <div key={`${tab}-${i}`} className="feature-card"
              onMouseEnter={e => { e.currentTarget.style.borderColor = f.c; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}>
              <div className="feat-icon-box" style={{ background: `${f.c}10` }}>{f.i}</div>
              <div><div className="feat-title">{f.t}</div><div className="feat-desc">{f.d}</div></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── QR ─── */
function QRSection({ t }) {
  const r1 = useReveal(), r2 = useReveal();
  const dots = [[45,5],[57,5],[69,5],[81,5],[45,17],[69,17],[45,29],[57,29],[5,45],[17,45],[29,45],[45,45],[57,45],[69,45],[81,45],[93,45],[105,45],[117,45],[5,57],[29,57],[57,57],[81,57],[105,57],[5,69],[17,69],[45,69],[69,69],[81,69],[93,69],[117,69],[5,81],[29,81],[57,81],[69,81],[105,81],[45,93],[57,93],[69,93],[93,93],[117,93],[45,105],[81,105],[105,105],[45,117],[57,117],[69,117],[93,117],[117,117]];
  return (
    <section id="qr" className="qr-section">
      <div className="section-inner">
        <div className="qr-grid">
          <div ref={r1} className="reveal">
            <span className="section-tag tag-violet">{t.qrTag}</span>
            <h2 className="section-heading">{t.qrTitle} <em>{t.qrTitleEm}</em></h2>
            <p className="section-desc" style={{ maxWidth: 440, marginBottom: '1.5rem' }}>{t.qrDesc}</p>
            <div className="qr-feats">
              {t.qrFeats.map((f, i) => (
                <div key={i} className="qr-feat-item">
                  <div className="qr-step-num">{i + 1}</div>
                  <div><div className="qr-feat-title">{f.t}</div><div className="qr-feat-desc">{f.d}</div></div>
                </div>
              ))}
            </div>
          </div>
          <div ref={r2} className="reveal qr-visual">
            <div className="qr-card">
              <div className="qr-card-accent" />
              <div className="qr-card-label">Smart QR Attendance System</div>
              <div className="qr-img-box">
                <div className="qr-scan-line" />
                <svg viewBox="0 0 130 130" width="125" height="125">
                  <rect x="5" y="5" width="30" height="30" fill="none" stroke="#6B48F5" strokeWidth="3" rx="3"/>
                  <rect x="9" y="9" width="22" height="22" fill="#6B48F5" rx="2"/>
                  <rect x="95" y="5" width="30" height="30" fill="none" stroke="#6B48F5" strokeWidth="3" rx="3"/>
                  <rect x="99" y="9" width="22" height="22" fill="#6B48F5" rx="2"/>
                  <rect x="5" y="95" width="30" height="30" fill="none" stroke="#6B48F5" strokeWidth="3" rx="3"/>
                  <rect x="9" y="99" width="22" height="22" fill="#6B48F5" rx="2"/>
                  {dots.map(([x, y], i) => <rect key={i} x={x} y={y} width="6" height="6" fill="#2A2A3A" rx="1"/>)}
                </svg>
              </div>
              <div className="qr-stats-grid">
                {[[t.qrStats[0], t.qrStats[1]], [t.qrStats[2], t.qrStats[3]], [t.qrStats[4], t.qrStats[5]], [t.qrStats[6], t.qrStats[7]]].map(([n, l], i) => (
                  <div key={i} className="qr-stat"><div className="qr-stat-num">{n}</div><div className="qr-stat-label">{l}</div></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Stats ─── */
function Stats({ t }) {
  const ref = useReveal();
  return (
    <div className="stats-bar">
      <div ref={ref} className="reveal stats-inner">
        {t.stats.map((s, i) => (
          <div key={i} className="stat-item">
            <div className="stat-num" style={{ color: s.color }}>{s.num}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── How It Works ─── */
function HowItWorks({ t }) {
  const [tab, setTab] = useState('user');
  const r1 = useReveal(), r2 = useReveal();
  const steps = tab === 'user' ? t.userSteps : t.vendorSteps;
  return (
    <section id="howitworks" className="how-section">
      <div className="how-inner">
        <span ref={r1} className="reveal section-tag tag-mint">{t.howTag}</span>
        <h2 ref={r1} className="reveal section-heading">{t.howTitle} <em>{t.howTitleEm}</em></h2>
        <div className="how-tabs">
          <button className={`how-tab${tab === 'user' ? ' active' : ''}`} onClick={() => setTab('user')}>{t.tabUserHow}</button>
          <button className={`how-tab${tab === 'vendor' ? ' active' : ''}`} onClick={() => setTab('vendor')}>{t.tabVendorHow}</button>
        </div>
        <div ref={r2} className="reveal how-steps-grid">
          <div className="how-connector" />
          {steps.map(([title, desc], i) => (
            <div key={`${tab}-${i}`} className="how-step">
              <div className="step-circle">{i + 1}</div>
              <div className="step-title">{title}</div>
              <div className="step-desc">{desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Testimonials ─── */
function Testimonials({ t }) {
  const r1 = useReveal(), r2 = useReveal();
  const typeColors = { Student: 'var(--coral)', Vendor: 'var(--mint)', Bachelor: 'var(--amber)', छात्र: 'var(--coral)', वेंडर: 'var(--mint)', बैचलर: 'var(--amber)', विद्यार्थी: 'var(--coral)', विक्रेता: 'var(--mint)', बॅचलर: 'var(--amber)' };
  const avBg = ['var(--coral)', 'var(--mint)', 'var(--amber)', '#6B48F5', '#FF7043', '#29B6F6'];
  return (
    <section id="reviews" className="testi-section">
      <div className="testi-inner">
        <span ref={r1} className="reveal section-tag tag-purple">{t.testiTag}</span>
        <h2 ref={r1} className="reveal section-heading">{t.testiTitle} <em>{t.testiTitleEm}</em></h2>
        <div ref={r2} className="reveal testi-grid">
          {t.testis.map((te, i) => {
            const tc = typeColors[te.type] || 'var(--coral)';
            return (
              <div key={i} className="testi-card"
                onMouseEnter={e => e.currentTarget.style.borderColor = tc}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                <div className="testi-type-badge" style={{ background: `${tc}10`, color: tc }}>{te.type}</div>
                <div className="testi-stars">{'★'.repeat(te.stars)}</div>
                <p className="testi-text">{te.text}</p>
                <div className="testi-author">
                  <div className="testi-av" style={{ background: avBg[i] }}>{te.name[0]}</div>
                  <div><div className="testi-name">{te.name}</div><div className="testi-role">{te.role}</div></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─── Download ─── */
function Download({ t }) {
  const r1 = useReveal(), r2 = useReveal();
  return (
    <section id="download" className="download-section">
      <div className="dl-orb" />
      <div className="dl-content">
        <div ref={r1} className="reveal">
          <span className="section-tag tag-coral" style={{ justifyContent: 'center' }}>{t.dlTag}</span>
          <h2 className="dl-heading">{t.dlTitle}</h2>
          <p className="dl-desc">{t.dlDesc}</p>
        </div>
        <div ref={r2} className="reveal dl-btns">
          <a href="https://play.google.com/store" target="_blank" rel="noreferrer" className="dl-btn">
            <span className="dl-icon">▶</span>
            <div><span className="dl-sub">Get it on</span><span className="dl-name">Google Play</span></div>
          </a>
          <div className="dl-btn disabled">
            <span className="dl-icon">🍎</span>
            <div><span className="dl-sub">Coming soon on</span><span className="dl-name">App Store</span></div>
          </div>
        </div>
        <p className="dl-note">{t.dlNote}</p>
      </div>
    </section>
  );
}

/* ─── Footer ─── */
function Footer({ t }) {
  return (
    <footer className="footer">
      <div className="footer-inner">
        <div className="footer-top">
          <div>
            <div className="footer-logo">
              <div className="f-logo-mark">🍽</div>
              <div className="f-logo-text">Swaad<span>India</span></div>
            </div>
            <p className="footer-tagline">{t.footerTagline}</p>
            <div className="footer-socials">
              {['𝕏', 'in', '▶'].map((ic, i) => <div key={i} className="social-btn">{ic}</div>)}
            </div>
          </div>
          {t.footerCols.map((col, ci) => (
            <div key={ci}>
              <div className="footer-col-title">{col.title}</div>
              <ul className="footer-links">{col.links.map((l, li) => <li key={li}>{l}</li>)}</ul>
            </div>
          ))}
        </div>
        <div className="footer-bottom">
          <div className="footer-copy">{t.footerCopy}</div>
          <div className="footer-cities">{t.footerCities}</div>
        </div>
      </div>
    </footer>
  );
}

/* ─── Root ─── */
export default function App() {
  const [lang, setLang] = useState('en');
  const t = T[lang];
  return (
    <div>
      <Navbar lang={lang} setLang={setLang} t={t} />
      <Hero t={t} />
      <Marquee />
      <Problem t={t} />
      <Features t={t} />
      <QRSection t={t} />
      <Stats t={t} />
      <HowItWorks t={t} />
      <Testimonials t={t} />
      <Download t={t} />
      <Footer t={t} />
    </div>
  );
}